/**
 * Configuración de permisos de usuarios para probar aseguradoras nuevas
 * Esta configuración permite controlar qué usuarios pueden ver qué aseguradoras
 * para propósitos de testing y lanzamiento gradual
 * 
 * APLICA PARA: CarsQuotation, MotoQuotation, TruckQuotation
 */

export type VehicleType = 'cars' | 'motos' | 'trucks';

export interface IUserInsurerPermission {
  email: string;
  allowedNewInsurers: string[];
  vehicleType: VehicleType;
  description?: string;
}

/**
 * Lista de aseguradoras nuevas que solo aparecen para usuarios específicos - AUTOS
 * Formato consistente con insures: { value, label }
 */
export const NEW_INSURERS_CARS: { value: string; label: string }[] = [
  // Agregar aquí nuevas aseguradoras para testing en Autos
];

/**
 * Lista de aseguradoras nuevas que solo aparecen para usuarios específicos - MOTOS
 * Formato consistente con insures: { value, label }
 */
export const NEW_INSURERS_MOTOS: { value: string; label: string }[] = [
  // SOLIDARIA_MOTOS ya está habilitado para todos los usuarios
];

/**
 * Lista de aseguradoras nuevas que solo aparecen para usuarios específicos - VEHICULOS PESADOS
 * Formato consistente con insures: { value, label }
 */
export const NEW_INSURERS_TRUCKS: { value: string; label: string }[] = [
  // Agregar aquí nuevas aseguradoras para testing en Vehículos Pesados
];

/**
 * Mantener compatibilidad con código existente
 */
export const NEW_INSURERS = NEW_INSURERS_CARS;

/**
 * Obtener la lista de aseguradoras nuevas según el tipo de vehículo
 */
export const getNewInsurersByType = (vehicleType: VehicleType): { value: string; label: string }[] => {
  switch (vehicleType) {
    case 'cars':
      return NEW_INSURERS_CARS;
    case 'motos':
      return NEW_INSURERS_MOTOS;
    case 'trucks':
      return NEW_INSURERS_TRUCKS;
    default:
      return [];
  }
};

/**
 * Lista de usuarios y las nuevas aseguradoras que pueden probar
 * APLICA PARA: CarsQuotation, MotoQuotation, TruckQuotation
 */
export const USER_INSURER_PERMISSIONS: IUserInsurerPermission[] = [
  // SOLIDARIA_MOTOS ya está habilitado para todos - permisos individuales removidos
];

/**
 * Función para obtener las aseguradoras existentes dinámicamente
 * Se ejecuta cuando se necesita para evitar importación circular
 * @param insuresArray - Array de aseguradoras desde resources.ts
 * @returns Array de strings con los valores de las aseguradoras existentes
 */
export const getExistingInsurers = (insuresArray: any[]): string[] => {
  return insuresArray.map(insurer => insurer.value);
};

/**
 * Función para obtener las aseguradoras permitidas para un usuario específico
 * @param userEmail - Email del usuario autenticado
 * @param insuresArray - Array de aseguradoras desde resources.ts
 * @param vehicleType - Tipo de vehículo ('cars', 'motos', 'trucks')
 * @returns Array de aseguradoras permitidas (existentes + nuevas permitidas)
 */
export const getUserAllowedInsurers = (
  userEmail: string, 
  insuresArray: any[], 
  vehicleType: VehicleType = 'cars'
): string[] => {
  if (!userEmail) {
    return getExistingInsurers(insuresArray);
  }

  const newInsurersForType = getNewInsurersByType(vehicleType);
  
  const userPermission = USER_INSURER_PERMISSIONS.find(
    permission => 
      permission.email.toLowerCase() === userEmail.toLowerCase() &&
      permission.vehicleType === vehicleType
  );
  
  let allowedInsurers = getExistingInsurers(insuresArray);
  
  if (userPermission && userPermission.allowedNewInsurers.length > 0 && newInsurersForType.length > 0) {
    const validNewInsurers = userPermission.allowedNewInsurers.filter(insurer => 
      newInsurersForType.map(newInsurer => newInsurer.value).includes(insurer)
    );
    allowedInsurers = [...allowedInsurers, ...validNewInsurers];
  }
  
  return allowedInsurers;
};

/**
 * Función para verificar si un usuario tiene acceso a una aseguradora específica
 * @param userEmail - Email del usuario autenticado
 * @param insurerName - Nombre de la aseguradora a verificar
 * @param insuresArray - Array de aseguradoras desde resources.ts
 * @param vehicleType - Tipo de vehículo ('cars', 'motos', 'trucks')
 * @returns true si el usuario tiene acceso, false si no
 */
export const canUserAccessInsurer = (
  userEmail: string, 
  insurerName: string, 
  insuresArray: any[],
  vehicleType: VehicleType = 'cars'
): boolean => {
  const existingInsurers = getExistingInsurers(insuresArray);
  
  if (existingInsurers.includes(insurerName)) {
    return true;
  }
  
  const userPermission = USER_INSURER_PERMISSIONS.find(
    permission => 
      permission.email.toLowerCase() === userEmail.toLowerCase() &&
      permission.vehicleType === vehicleType
  );
  
  return userPermission ? userPermission.allowedNewInsurers.includes(insurerName) : false;
};

/**
 * Función genérica para obtener aseguradoras filtradas según tipo de vehículo
 * @param userEmail - Email del usuario autenticado
 * @param insuresArray - Array de aseguradoras base
 * @param vehicleType - Tipo de vehículo
 * @returns Array de aseguradoras disponibles para el usuario
 */
export const getFilteredInsurersByType = (
  userEmail: string | undefined,
  insuresArray: { value: string; label: string }[],
  vehicleType: VehicleType
): { value: string; label: string }[] => {
  if (!userEmail) {
    return insuresArray;
  }

  const newInsurersForType = getNewInsurersByType(vehicleType);
  const allowedInsurers = getUserAllowedInsurers(userEmail, insuresArray, vehicleType);
  
  let allAvailableInsurers = [...insuresArray];
  
  if (allowedInsurers.length > insuresArray.length && newInsurersForType.length > 0) {
    const newInsurersForUser = newInsurersForType.filter(insurer => 
      allowedInsurers.includes(insurer.value)
    );
    allAvailableInsurers = [...insuresArray, ...newInsurersForUser];
  }
  
  return allAvailableInsurers;
};

